import { c as _c } from "react/compiler-runtime";
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { flagsContext } from '@/contexts/flagsContext';
import { parseJson } from '@/utils/string';
import { useEnvironment } from './useEnvironment';

// Splits a ?flags= query string into key→value pairs, handling JSON object/array
// values whose commas and colons would otherwise be misinterpreted as delimiters.
const parseFlagOverrides = (raw: string): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  let i = 0;
  while (i < raw.length) {
    let depth = 0;
    let j = i;
    while (j < raw.length && !(raw[j] === ',' && depth === 0)) {
      if (raw[j] === '{' || raw[j] === '[') {
        depth++;
      } else if (raw[j] === '}' || raw[j] === ']') {
        depth--;
      }
      j++;
    }
    const entry = raw.slice(i, j);
    const sep = entry.indexOf(':');
    if (sep === -1) {
      result[entry] = true;
    } else {
      const key = entry.slice(0, sep);
      const value = entry.slice(sep + 1);
      const parsed = parseJson(value);
      result[key] = parsed === undefined ? value : parsed;
    }
    i = j + 1;
  }
  return result;
};
export const useFeatureFlags = () => {
  const $ = _c(6);
  const conductedFlags = useFlags(flagsContext);
  let t0;
  let t1;
  if ($[0] !== conductedFlags) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const flagsOverrides = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("flags");
      if (!flagsOverrides) {
        t1 = conductedFlags;
        break bb0;
      }
      t0 = parseFlagOverrides(flagsOverrides);
    }
    $[0] = conductedFlags;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  const flags = t0;
  let t2;
  if ($[3] !== conductedFlags || $[4] !== flags) {
    t2 = {
      ...conductedFlags,
      ...flags
    };
    $[3] = conductedFlags;
    $[4] = flags;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const useLaunchDarkly = () => {
  const $ = _c(5);
  const {
    isDemo,
    isDev,
    isTest
  } = useEnvironment();
  const [ldClientReady, setLDClientReady] = useState(isDemo || isDev || isTest);
  const ldClient = useLDClient(flagsContext);
  let t0;
  let t1;
  if ($[0] !== ldClient) {
    t0 = () => {
      ldClient?.waitUntilReady().then(() => setLDClientReady(true));
    };
    t1 = [ldClient];
    $[0] = ldClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== ldClientReady) {
    t2 = {
      isReady: ldClientReady
    };
    $[3] = ldClientReady;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};